import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs'

export default class extends Controller {
  static targets = [
    "canvas", "steps", "notifications", "workflowSteps",
    "slideover", "slideoverTitle", "actionConfig",
    "triggerType", "entityType", "fieldCondition", "fieldName", "fieldOperator", "fieldValue",
    "triggerConfig", "scheduleConfig", "scheduleTime", "scheduleDayOfWeek", "scheduleDayOfMonth", "scheduleTimezone", "weeklyConfig", "monthlyConfig",
    "taskConfig", "taskAssignee", "taskTitle", "taskDescription", "taskDueDate", "taskPriority", "taskCategory",
    "emailConfig", "emailRecipient", "emailSubject", "emailBody",
    "poolConfig", "targetPool",
    "smsConfig", "smsRecipient", "smsMessage",
    "delayConfig", "delayDays", "delayBusinessDays", "delayReason",
    "integrationConfig", "integrationType", "businessCentralConfig", "bcAction", "webhookConfig", "webhookUrl", "inrivaOnboardingConfig", "onboardingProcess", "integrationPayload", "hrflowConfig", "synthflowConfig", "slackConfig", "teamsConfig", "pipedriveConfig",
    "formTriggerType", "formEntityType", "formFieldConditions", "formScheduleTime", "formScheduleDayOfWeek", "formScheduleDayOfMonth", "formScheduleTimezone",
    "workflowForm", "formTitle", "formDescription", "formCategory", "formActive", "formWorkflowData", "formWorkflowSteps", "formWorkflowConfig", "formSubmit",
    "workflowTitle", "workflowDescription", "statusSidebar", "aiModal", "aiInput", "aiPreview", "aiActions"
  ]

  connect() {
    this.initializeDragAndDrop()
    this.initializeKeyboardShortcuts()
    this.currentStepId = 2 // Start mit ID 2, da Trigger ID 1 hat
    this.stepCounter = 2
  }

  // Slideover Management
  openSlideover(actionType, stepId = null) {
    this.currentActionType = actionType
    this.currentStepId = stepId || this.generateStepId()
    
    // Titel setzen
    const actionTitles = {
      'create_task': 'Aufgabe erstellen',
      'send_email': 'E-Mail senden',
      'move_pool': 'Pool verschieben',
      'send_sms': 'SMS senden',
      'add_tag': 'Tag hinzufügen',
      'send_notification': 'Benachrichtigung senden',
      'add_delay': 'Verzögerung hinzufügen',
      'add_condition': 'Bedingung hinzufügen',
      'add_integration': 'Integration hinzufügen'
    }
    
    this.slideoverTitleTarget.textContent = actionTitles[actionType] || 'Aktion konfigurieren'
    
    // Alle Konfigurationen ausblenden
    this.hideAllConfigs()
    
    // Spezifische Konfiguration anzeigen
    switch(actionType) {
      case 'create_task':
        this.taskConfigTarget.style.display = 'block'
        break
      case 'send_email':
        this.emailConfigTarget.style.display = 'block'
        break
      case 'move_pool':
        this.poolConfigTarget.style.display = 'block'
        break
      case 'send_sms':
        this.smsConfigTarget.style.display = 'block'
        break
      case 'add_delay':
        this.delayConfigTarget.style.display = 'block'
        break
      case 'add_integration':
        this.integrationConfigTarget.style.display = 'block'
        break
    }
    
    // Slideover anzeigen
    this.slideoverTarget.style.display = 'block'
    document.body.style.overflow = 'hidden'
  }

  closeSlideover() {
    this.slideoverTarget.style.display = 'none'
    document.body.style.overflow = 'auto'
  }

  hideAllConfigs() {
    this.triggerConfigTarget.style.display = 'none'
    this.taskConfigTarget.style.display = 'none'
    this.emailConfigTarget.style.display = 'none'
    this.poolConfigTarget.style.display = 'none'
    this.smsConfigTarget.style.display = 'none'
    this.delayConfigTarget.style.display = 'none'
    this.integrationConfigTarget.style.display = 'none'
    this.businessCentralConfigTarget.style.display = 'none'
    this.webhookConfigTarget.style.display = 'none'
    this.inrivaOnboardingConfigTarget.style.display = 'none'
    this.hrflowConfigTarget.style.display = 'none'
    this.synthflowConfigTarget.style.display = 'none'
    this.slackConfigTarget.style.display = 'none'
    this.teamsConfigTarget.style.display = 'none'
    this.pipedriveConfigTarget.style.display = 'none'
  }

  // Trigger Event Handlers
  onTriggerTypeChange(event) {
    const triggerType = event.target.value
    
    // Alle Trigger-Konfigurationen ausblenden
    this.fieldConditionTarget.style.display = 'none'
    this.scheduleConfigTarget.style.display = 'none'
    
    // Wöchentliche und monatliche Konfigurationen ausblenden
    if (this.hasTarget('weeklyConfig')) this.weeklyConfigTarget.style.display = 'none'
    if (this.hasTarget('monthlyConfig')) this.monthlyConfigTarget.style.display = 'none'
    
    // Spezifische Konfiguration anzeigen
    if (triggerType === 'field_changed') {
      this.fieldConditionTarget.style.display = 'block'
    } else if (['daily', 'weekly', 'monthly'].includes(triggerType)) {
      this.scheduleConfigTarget.style.display = 'block'
      
      // Spezifische Zeitplan-Konfigurationen anzeigen
      if (triggerType === 'weekly' && this.hasTarget('weeklyConfig')) {
        this.weeklyConfigTarget.style.display = 'block'
      } else if (triggerType === 'monthly' && this.hasTarget('monthlyConfig')) {
        this.monthlyConfigTarget.style.display = 'block'
      }
    }
    
    // Trigger type changed
  }

  onEntityTypeChange(event) {
    // Hier können wir dynamisch die verfügbaren Felder basierend auf der Entität laden
    const entityType = event.target.value
    // Entity type changed
  }

  onIntegrationTypeChange(event) {
    const integrationType = event.target.value
    
    // Alle Integration-Konfigurationen ausblenden
    this.businessCentralConfigTarget.style.display = 'none'
    this.webhookConfigTarget.style.display = 'none'
    this.inrivaOnboardingConfigTarget.style.display = 'none'
    this.hrflowConfigTarget.style.display = 'none'
    this.synthflowConfigTarget.style.display = 'none'
    this.slackConfigTarget.style.display = 'none'
    this.teamsConfigTarget.style.display = 'none'
    this.pipedriveConfigTarget.style.display = 'none'
    
    // Spezifische Konfiguration anzeigen
    switch(integrationType) {
      case 'business_central':
        this.businessCentralConfigTarget.style.display = 'block'
        break
      case 'webhook':
        this.webhookConfigTarget.style.display = 'block'
        break
      case 'inriva_onboarding':
        this.inrivaOnboardingConfigTarget.style.display = 'block'
        break
      case 'hrflow':
        this.hrflowConfigTarget.style.display = 'block'
        break
      case 'synthflow':
        this.synthflowConfigTarget.style.display = 'block'
        break
      case 'slack':
        this.slackConfigTarget.style.display = 'block'
        break
      case 'teams':
        this.teamsConfigTarget.style.display = 'block'
        break
      case 'pipedrive':
        this.pipedriveConfigTarget.style.display = 'block'
        break
    }
  }

  // Action Configuration Methods
  addTaskAction() {
    this.openSlideover('create_task')
  }

  addEmailAction() {
    this.openSlideover('send_email')
  }

  addPoolAction() {
    this.openSlideover('move_pool')
  }

  addSmsAction() {
    this.openSlideover('send_sms')
  }

  addTagAction() {
    this.openSlideover('add_tag')
  }

  addNotificationAction() {
    this.openSlideover('send_notification')
  }

  addDelayAction() {
    this.openSlideover('add_delay')
  }

  addConditionAction() {
    this.openSlideover('add_condition')
  }

  addIntegrationAction() {
    this.openSlideover('add_integration')
  }

  // Save Configuration
  saveActionConfig() {
    const config = this.getCurrentConfig()
    
    if (this.currentActionType === 'trigger') {
      // Trigger-Konfiguration speichern
      this.saveTriggerConfig(config)
    } else if (this.currentStepId && this.workflowStepsTarget.querySelector(`[data-step-id="${this.currentStepId}"]`)) {
      // Bestehenden Schritt aktualisieren
      this.updateExistingStep(this.currentStepId, config)
    } else {
      // Neuen Schritt erstellen
      this.createNewStep(config)
    }
    
    this.closeSlideover()
    this.showNotification('Konfiguration erfolgreich gespeichert', 'success')
  }

  saveTriggerConfig(config) {
    // Trigger-Konfiguration in versteckte Formularfelder speichern
    if (this.hasTarget('formTriggerType')) {
      this.formTriggerTypeTarget.value = config.triggerType
    }
    if (this.hasTarget('formEntityType')) {
      this.formEntityTypeTarget.value = config.entityType
    }
    if (this.hasTarget('formFieldConditions')) {
      this.formFieldConditionsTarget.value = JSON.stringify(config.fieldCondition || {})
    }
    if (this.hasTarget('formScheduleTime')) {
      this.formScheduleTimeTarget.value = config.scheduleConfig?.time || ''
    }
    if (this.hasTarget('formScheduleDayOfWeek')) {
      this.formScheduleDayOfWeekTarget.value = config.scheduleConfig?.dayOfWeek || ''
    }
    if (this.hasTarget('formScheduleDayOfMonth')) {
      this.formScheduleDayOfMonthTarget.value = config.scheduleConfig?.dayOfMonth || ''
    }
    if (this.hasTarget('formScheduleTimezone')) {
      this.formScheduleTimezoneTarget.value = config.scheduleConfig?.timezone || 'Europe/Berlin'
    }
    
    // Trigger-Anzeige aktualisieren
    this.updateTriggerDisplay(config)
  }

  updateTriggerDisplay(config) {
    const triggerElement = document.querySelector('.workflow-trigger')
    if (triggerElement) {
      const descriptionElement = triggerElement.querySelector('p')
      if (descriptionElement) {
        const triggerTypeName = this.getTriggerTypeName(config.triggerType)
        const entityTypeName = this.getEntityTypeName(config.entityType)
        descriptionElement.textContent = `${triggerTypeName} - ${entityTypeName}`
      }
    }
  }

  getTriggerTypeName(triggerType) {
    const names = {
      'record_created': 'Datensatz erstellt',
      'record_updated': 'Datensatz aktualisiert',
      'field_changed': 'Feld geändert',
      'status_changed': 'Status geändert',
      'pool_changed': 'Pool geändert',
      'email_sent': 'E-Mail gesendet',
      'interview_scheduled': 'Interview geplant',
      'daily': 'Täglich',
      'weekly': 'Wöchentlich',
      'monthly': 'Monatlich'
    }
    return names[triggerType] || triggerType
  }

  getEntityTypeName(entityType) {
    const names = {
      'applicant': 'Bewerber',
      'job': 'Stelle',
      'customer': 'Kunde',
      'company': 'Unternehmen',
      'contact': 'Kontakt',
      'quote': 'Angebot'
    }
    return names[entityType] || entityType
  }

  getCurrentConfig() {
    const baseConfig = {
      triggerType: this.triggerTypeTarget.value,
      entityType: this.entityTypeTarget.value,
      actionType: this.currentActionType
    }

    // Feld-Bedingung hinzufügen
    if (baseConfig.triggerType === 'field_changed') {
      baseConfig.fieldCondition = {
        field: this.fieldNameTarget.value,
        operator: this.fieldOperatorTarget.value,
        value: this.fieldValueTarget.value
      }
    }

    // Zeit-basierte Trigger-Konfiguration hinzufügen
    if (['daily', 'weekly', 'monthly'].includes(baseConfig.triggerType)) {
      baseConfig.scheduleConfig = {
        time: this.scheduleTimeTarget.value,
        dayOfWeek: this.scheduleDayOfWeekTarget.value,
        dayOfMonth: this.scheduleDayOfMonthTarget.value,
        timezone: this.scheduleTimezoneTarget.value
      }
    }

    // Aktion-spezifische Konfiguration
    switch(this.currentActionType) {
      case 'create_task':
        baseConfig.taskConfig = {
          assignee: this.taskAssigneeTarget.value,
          title: this.taskTitleTarget.value,
          description: this.taskDescriptionTarget.value,
          dueDate: this.taskDueDateTarget.value,
          priority: this.taskPriorityTarget.value,
          category: this.taskCategoryTarget.value
        }
        break
      case 'send_email':
        baseConfig.emailConfig = {
          recipient: this.emailRecipientTarget.value,
          subject: this.emailSubjectTarget.value,
          body: this.emailBodyTarget.value
        }
        break
      case 'move_pool':
        baseConfig.poolConfig = {
          targetPool: this.targetPoolTarget.value
        }
        break
      case 'send_sms':
        baseConfig.smsConfig = {
          recipient: this.smsRecipientTarget.value,
          message: this.smsMessageTarget.value
        }
        break
    }

    return baseConfig
  }

  createNewStep(config) {
    const stepId = this.generateStepId()
    const stepHtml = this.createStepHtml(stepId, config)
    
    // Schritt zum Canvas hinzufügen
    this.workflowStepsTarget.insertAdjacentHTML('beforeend', stepHtml)
    
    this.stepCounter++
  }

  updateExistingStep(stepId, config) {
    const stepElement = this.workflowStepsTarget.querySelector(`[data-step-id="${stepId}"]`)
    if (stepElement) {
      const newStepHtml = this.createStepHtml(stepId, config)
      stepElement.outerHTML = newStepHtml
    }
  }

  createStepHtml(stepId, config) {
    const stepType = this.getStepType(config.actionType)
    const configSummary = this.getConfigSummary(config)
    
    return `
      <div class="workflow-step mb-12 flex justify-center" data-step-id="${stepId}">
        <div class="flex flex-col items-center w-full max-w-md">
          <!-- Verbindungslinie mit Pfeil -->
          <div class="relative w-full flex justify-center mb-4">
            <div class="w-0.5 h-12 bg-gradient-to-b from-blue-400 to-blue-600 relative">
              <!-- Pfeilspitze -->
              <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-600"></div>
            </div>
          </div>
          
          <!-- Node -->
          <div class="bg-white rounded-xl shadow-lg border-2 border-gray-100 p-6 w-full hover:shadow-xl transition-all duration-300 hover:border-blue-200 hover:scale-105">
            <div class="flex items-center justify-between">
              <div class="flex items-center flex-1">
                <div class="${stepType.bgColor} text-white rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold mr-4 shadow-lg ring-4 ring-white">${stepId}</div>
                <div class="flex-1">
                  <div class="text-lg font-semibold text-gray-800 mb-1">${stepType.title}</div>
                  <div class="text-sm text-gray-600 leading-relaxed">${configSummary}</div>
                </div>
              </div>
              <div class="flex items-center space-x-2 ml-4">
                <button class="text-gray-400 hover:text-blue-600 p-2 rounded-full transition-all duration-200 hover:bg-blue-50" data-action="click->workflow-designer#editStep" data-step-id="${stepId}" title="Bearbeiten">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                  </svg>
                </button>
                <button class="text-gray-400 hover:text-red-600 p-2 rounded-full transition-all duration-200 hover:bg-red-50" data-action="click->workflow-designer#deleteStep" data-step-id="${stepId}" title="Löschen">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }

  getStepType(actionType) {
    const types = {
      'create_task': {
        title: 'Aufgabe erstellen',
        bgColor: 'bg-purple-500',
        borderColor: 'border-purple-200',
        icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
      },
      'send_email': {
        title: 'E-Mail senden',
        bgColor: 'bg-blue-500',
        borderColor: 'border-blue-200',
        icon: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
      },
      'move_pool': {
        title: 'Pool verschieben',
        bgColor: 'bg-green-500',
        borderColor: 'border-green-200',
        icon: 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4'
      },
      'send_sms': {
        title: 'SMS senden',
        bgColor: 'bg-yellow-500',
        borderColor: 'border-yellow-200',
        icon: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z'
      }
    }
    
    return types[actionType] || {
      title: 'Aktion',
      bgColor: 'bg-gray-500',
      borderColor: 'border-gray-200',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z'
    }
  }

  getConfigSummary(config) {
    switch(config.actionType) {
      case 'create_task':
        const taskConfig = config.taskConfig
        return `Aufgabe "${taskConfig.title}" an ${taskConfig.assignee}`
      case 'send_email':
        const emailConfig = config.emailConfig
        return `E-Mail "${emailConfig.subject}" an ${emailConfig.recipient}`
      case 'move_pool':
        const poolConfig = config.poolConfig
        return `Verschieben in Pool "${poolConfig.targetPool}"`
      case 'send_sms':
        const smsConfig = config.smsConfig
        return `SMS an ${smsConfig.recipient}`
      default:
        return 'Konfiguriert'
    }
  }

  // Step Management
  editStep(event) {
    const stepId = event.currentTarget.dataset.stepId
    // Hier würden wir die gespeicherte Konfiguration laden
    // Für jetzt öffnen wir einfach den Slideover
    this.openSlideover('create_task', stepId)
  }

  deleteStep(event) {
    const stepId = event.currentTarget.dataset.stepId
    const stepElement = this.workflowStepsTarget.querySelector(`[data-step-id="${stepId}"]`)
    
    if (stepElement && confirm('Schritt wirklich löschen?')) {
      stepElement.remove()
      this.updateStepNumbers()
      this.showNotification('Schritt gelöscht', 'success')
    }
  }

  generateStepId() {
    return this.stepCounter++
  }

  updateStepNumbers() {
    const steps = this.workflowStepsTarget.querySelectorAll('.workflow-step')
    steps.forEach((step, index) => {
      const numberElement = step.querySelector('.rounded-full')
      if (numberElement) {
        numberElement.textContent = index + 2 // +2 weil Trigger ID 1 hat
      }
    })
  }

  // Drag & Drop für Schritt-Reihenfolge
  initializeDragAndDrop() {
    if (this.workflowStepsTarget) {
      Sortable.create(this.workflowStepsTarget, {
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        onEnd: (event) => {
          this.updateStepNumbers()
          this.showNotification('Reihenfolge aktualisiert', 'info')
        }
      })
    }
  }

  // Keyboard Shortcuts
  initializeKeyboardShortcuts() {
    document.addEventListener('keydown', (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch(event.key) {
          case 's':
            event.preventDefault()
            this.saveWorkflow()
            break
          case 'z':
            event.preventDefault()
            this.undo()
            break
          case 'p':
            event.preventDefault()
            this.previewWorkflow()
            break
        }
      }
    })
  }

  // Trigger Management
  editTrigger() {
    this.currentActionType = 'trigger'
    this.slideoverTitleTarget.textContent = 'Trigger konfigurieren'
    
    // Alle Konfigurationen ausblenden
    this.hideAllConfigs()
    
    // Trigger-Konfiguration anzeigen
    this.triggerConfigTarget.style.display = 'block'
    
    // Slideover anzeigen
    this.slideoverTarget.style.display = 'block'
    document.body.style.overflow = 'hidden'
  }

  // Workflow Actions
  saveWorkflow() {
    this.showNotification('Workflow gespeichert', 'success')
  }

  previewWorkflow() {
    this.showNotification('Vorschau wird generiert...', 'info')
  }

  undo() {
    this.showNotification('Letzte Aktion rückgängig gemacht', 'info')
  }

  // Additional methods referenced in the view
  saveWorkflowTitle() {
    if (this.hasTarget('formTitle')) {
      this.formTitleTarget.value = this.workflowTitleTarget.value
    }
  }

  saveWorkflowDescription() {
    if (this.hasTarget('formDescription')) {
      this.formDescriptionTarget.value = this.workflowDescriptionTarget.value
    }
  }

  showAiModal() {
    this.showNotification('KI-Assistent wird geöffnet...', 'info')
  }

  showActionMenu() {
    this.showNotification('Aktionsmenü wird geöffnet...', 'info')
  }

  closeStatusSidebar() {
    if (this.hasTarget('statusSidebar')) {
      this.statusSidebarTarget.classList.add('translate-x-full')
    }
  }

  // Notifications
  showNotification(message, type = 'info') {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500'
    }

    const notification = document.createElement('div')
    notification.className = `${colors[type]} text-white px-4 py-2 rounded-md shadow-lg mb-2 transform transition-all duration-300 translate-x-full`
    notification.textContent = message

    this.notificationsTarget.appendChild(notification)

    // Animation
    setTimeout(() => {
      notification.classList.remove('translate-x-full')
    }, 100)

    // Auto-remove
    setTimeout(() => {
      notification.classList.add('translate-x-full')
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 300)
    }, 3000)
  }
}
